import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useClassrooms } from "./ClassroomContext";
import useAutoRetry from "../hooks/useAutoRetry";
import { RETRYABLE, problemOf } from "./problems";
import { readRelease, releaseActivity } from "./releaseService";
import { recordFailure } from "./syncDiagnostics";

// status: "checking" | "not-released" | "releasing" | "released" | "problem"
// Read from the server every time the block is shown, so a refresh, reload or
// reopened delivery shows what is actually saved.
const useRelease = ({ lessonId, record, activityId }) => {
  const { user } = useAuth();
  const classroomId = useClassrooms().active.id;
  const [state, setState] = useState({ status: "checking", problem: null });
  const [attempt, setAttempt] = useState(0);
  const recordRef = useRef(record);
  recordRef.current = record;
  const syncKey = record?.syncKey;
  const enabled = Boolean(activityId && syncKey);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    readRelease({ record: recordRef.current, activityId }).then(
      (release) => {
        if (!active) return;
        setState((s) =>
          s.status === "releasing"
            ? s
            : { status: release ? "released" : "not-released", problem: null }
        );
      },
      (error) => {
        if (!active) return;
        recordFailure(`release:${lessonId}`, error);
        setState((s) =>
          s.status === "releasing"
            ? s
            : { status: "problem", problem: problemOf(error), reading: true }
        );
      }
    );
    return () => {
      active = false;
    };
  }, [enabled, syncKey, activityId, lessonId, attempt]);

  const release = useCallback(async () => {
    setState({ status: "releasing", problem: null });
    try {
      await releaseActivity({
        record: recordRef.current,
        lessonId,
        teacherId: user.id,
        classroomId,
        activityId,
      });
      setState({ status: "released", problem: null });
    } catch (error) {
      recordFailure(`release:${lessonId}`, error);
      setState({ status: "problem", problem: problemOf(error) });
    }
  }, [lessonId, user.id, classroomId, activityId]);

  // Only failed reads retry by themselves; a failed release waits for the
  // teacher, who decides whether the moment is still right.
  const recheck = useCallback(() => setAttempt((n) => n + 1), []);
  useAutoRetry(
    state.status === "problem" && state.reading && RETRYABLE.has(state.problem),
    recheck
  );

  return { ...state, release, recheck };
};

export default useRelease;
